import logoAsset from "@/assets/logo.png.asset.json";
import { Link } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { 
  User, 
  ChevronDown, 
  LogOut, 
  LayoutDashboard,
  Menu,
  X,
  Star,
  Megaphone,
  BookOpen,
  Building2
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useState } from "react";
import { useLocation } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

export function Header() {
  const { user, profile, authState, signOut } = useAuth();
  const loading = authState === 'carregando';
  const queryClient = useQueryClient();
  const location = useLocation();

  const { data: empresa } = useQuery({
    queryKey: ['minha-empresa-header', user?.id],
    queryFn: async () => {
      const { data } = await supabase.from('fornecedores').select('id').eq('owner_id', user?.id).maybeSingle();
      return data;
    },
    enabled: !!user?.id
  });


  const prefetchFornecedores = () => {
    queryClient.prefetchQuery({
      queryKey: ['fornecedores-initial'],
      queryFn: async () => {
        const { data } = await supabase
          .from('fornecedores')
          .select('id, nome, cidade, uf, polo, verificado, destaque, logo_url, segmentos(nome, icone)')
          .eq('status', 'aprovado')
          .order('destaque', { ascending: false })
          .range(0, 23);
        return data;
      },
      staleTime: 1000 * 60 * 5,
    });
  };
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: "Fornecedores", href: "/fornecedores", icon: null },
    { name: "Favoritos", href: "/favoritos", icon: Star },
    { name: "Guias", href: "/guias", icon: BookOpen },
    { name: "Quero Anunciar", href: "/quero-anunciar", icon: Megaphone },
  ];

  const renderNavLinks = (className?: string) => {
    const isAprovado = profile?.status === 'aprovado';
    
    // Filter logic:
    // If approved: Fornecedores, Favoritos, Guias, Quero Anunciar
    // If not approved/guest: Guias, Quero Anunciar
    const visibleLinks = isAprovado 
      ? navLinks 
      : navLinks.filter(link => ['/guias', '/quero-anunciar'].includes(link.href));

    return (
      <div className={cn("flex items-center gap-8", className)}>
        {visibleLinks.map((link) => {
          const isActive = location.pathname === link.href;
          const Icon = link.icon;
          
          if (link.href === '/quero-anunciar' && className?.includes('hidden md:flex')) {
            return (
              <Link key={link.href} to={link.href}>
                <Button className="bg-[#C6F764] hover:bg-[#C6F764]/90 text-[#0C1A12] font-bold rounded-xl flex items-center gap-2 shadow-sm">
                  {Icon && <Icon className="w-4 h-4" />}
                  {link.name}
                </Button>
              </Link>
            );
          }

          return (
            <Link 
              key={link.href}
              to={link.href} 
              onMouseEnter={link.href === '/fornecedores' ? prefetchFornecedores : undefined}
              className={cn(
                "text-[15px] font-bold transition-all relative py-1 flex items-center gap-2",
                isActive 
                  ? "text-[#0C1A12]" 
                  : "text-[#6B7280] hover:text-[#0C1A12]"
              )}
            >
              {Icon && <Icon className="w-4 h-4" />}
              {link.name}
              {isActive && (
                <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-[#C6F764]" />
              )}
            </Link>
          );
        })}
      </div>
    );
  };

  const renderAuth = () => {
    // If no session is being loaded or we already have a user, render immediately
    if (!user && !loading) {
      return (
        <div className="flex items-center gap-4">
          <Link to="/entrar">
            <Button variant="outlineLight" className="rounded-full px-6 h-10">
              Entrar
            </Button>
          </Link>
          <Link to="/cadastro">
            <Button variant="onLight" className="rounded-full px-6 h-10">
              Criar meu acesso
            </Button>
          </Link>
        </div>

      );
    }

    if (loading && !user) {
      return (
        <div className="flex items-center gap-4">
          <Skeleton className="w-20 h-4" />
          <Skeleton className="w-24 h-10 rounded-xl" />
        </div>
      );
    }


    return (
      <div className="flex items-center gap-4">
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-2 p-1 rounded-full hover:bg-secondary transition-all outline-none">
            {profile ? (
              <div className="w-8 h-8 bg-brand-dark rounded-full flex items-center justify-center text-primary font-bold text-xs">
                {profile.nome?.[0] || 'U'}
              </div>
            ) : (
              <Skeleton className="w-8 h-8 rounded-full" />
            )}
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 rounded-2xl p-2 border-border shadow-xl">
            <DropdownMenuLabel className="px-3 py-2">
              <span className="block text-sm font-bold text-brand-dark">
                {profile ? profile.nome : <Skeleton className="h-4 w-24" />}
              </span>
              <span className="block text-xs text-muted-foreground truncate">{user.email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild className="rounded-xl cursor-pointer py-2 px-3 focus:bg-secondary">
              <Link to="/minha-conta" search={{ aba: 'dados' }}>
                <User className="mr-2 h-4 w-4" /> Minha conta
              </Link>
            </DropdownMenuItem>

            {empresa && (
              <DropdownMenuItem asChild className="rounded-xl cursor-pointer py-2 px-3 focus:bg-secondary">
                <Link to="/minha-conta" search={{ aba: 'empresa' }}>
                  <Building2 className="mr-2 h-4 w-4" /> Minha empresa
                </Link>
              </DropdownMenuItem>
            )}


            {profile && ['admin', 'super_admin'].includes(profile.role) && (
              <DropdownMenuItem asChild className="rounded-xl cursor-pointer py-2 px-3 focus:bg-secondary">
                <Link to="/admin">
                  <LayoutDashboard className="mr-2 h-4 w-4" /> Painel admin
                </Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => signOut()} className="rounded-xl cursor-pointer py-2 px-3 focus:bg-destructive/10 text-destructive">
              <LogOut className="mr-2 h-4 w-4" /> Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-white/80 backdrop-blur-md">
      <nav className="container mx-auto px-4 h-24 flex items-center justify-between">
        <Link to="/" className="flex items-center shrink-0">
          <img 
            src={logoAsset.url} 
            alt="Gopage" 
            className="h-[60px] w-[60px] object-contain"
            width={60}
            height={60}
            loading="eager"
            // @ts-ignore
            fetchpriority="high"
          />
        </Link>

        {/* Desktop Nav */}
        {renderNavLinks("hidden md:flex")}

        <div className="flex items-center gap-4">
          {renderAuth()}
          
          {/* Mobile Menu Toggle */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] p-0">
              <div className="flex flex-col h-full p-6">
                <div className="flex items-center justify-between mb-8">
                  <img src={logoAsset.url} alt="Gopage" className="h-10 w-10 rounded-lg" />
                  <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)}>
                    <X className="w-6 h-6" />
                  </Button>
                </div>
                <div className="flex flex-col gap-4">
                  {navLinks.map((link) => {
                    const isAprovado = profile?.status === 'aprovado';
                    const isPublic = ['/guias', '/quero-anunciar'].includes(link.href);
                    if (!isAprovado && !isPublic) return null;
                    
                    return (
                      <Link 
                        key={link.href}
                        to={link.href} 
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={cn(
                          "text-lg font-bold p-4 rounded-2xl transition-all",
                          location.pathname === link.href ? "bg-[#C6F764]/10 text-[#0C1A12]" : "text-[#1A1A1A] hover:bg-secondary"
                        )}
                      >
                        {link.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}